import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import fuzzysort from "fuzzysort";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { MobileHome } from "@/components/mobile/MobileHome";
import type {
  AgentRuntimeAttachedPullRequest,
  AgentRuntimePreferredMcpServerRef,
  AgentRuntimeRequestedSkillRef,
  AgentRuntimeScratchPadRef,
  AgentRuntimeSessionContextRef,
  StoredSessionIntent,
} from "@cord/shared";
import { toast } from "sonner";
import { useDefaultAgentType, getInitialAgentType, type AgentType } from "@/hooks/use-default-agent";
import {
  Plus,
  ChevronDown,
  Search,
  ArrowUp,
  Check,
  ArrowRight,
  Menu,
  GitBranch,
  Github,
  Loader2,
  MoreHorizontal,
  Sparkles,
} from "lucide-react";
import {
  Microphone,
  SpinnerGap,
  ArrowClockwise,
  CaretRight,
  CaretDown,
  X,
  Brain,
  File as FileIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { normalizeArrayResponse } from "@/lib/repository-route-response-normalizers";
import { authClient } from "@/lib/auth";
import { hasSeenModelsStep, hasSkippedGithubStep, hasSkippedRepoStep } from "@/lib/onboarding-progress";
import { cn, formatSessionTitle, relativeTime } from "@/lib/utils";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import { useOnboardingProgress } from "@/hooks/use-onboarding-progress";
import { useSessionUI } from "@/stores/session-ui";
import { useVoiceTranscription } from "@/hooks/use-voice-transcription";
import { useProviders, type Provider, type ProviderModel } from "@/hooks/use-providers";
import { useModelPrefs } from "@/stores/model-prefs";
import { ModelSelectorPopover } from "@/components/chat/ModelSelectorPopover";
import {
  formatThinkingVariantLabel,
  getThinkingVariants,
  normalizeThinkingVariant,
  resolveEffectiveThinkingVariant,
  THINKING_OFF_VARIANT,
} from "@/config/model-capabilities";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  setExplicitSessionIntent,
} from "@/lib/session-model-storage";
import { AudioWaveform } from "@/components/chat/AudioWaveform";
import { useHasProvider } from "@/hooks/use-has-provider";
import { useDeferredBackgroundActivityEnabled } from "@/hooks/use-deferred-background-activity";
import {
  ANALYTICS_EVENTS,
  buildThreadAnalyticsProperties,
  captureAnalyticsEvent,
} from "@/lib/analytics";
import {
  buildOptimisticEmptyThreadSessionRecord,
  mergeSessionRecordPreservingHasHistory,
} from "@/lib/optimistic-empty-thread-session-record";
import {
  applyOptimisticSessionMembershipToSessionListCaches,
  captureSessionCacheMutationSnapshot,
  restoreSessionCacheMutationSnapshot,
  type SessionCacheMutationSnapshot,
} from "@/lib/session-membership-cache";
import {
  isAllowedImageMime,
  isAllowedMessageAttachmentMime,
  resolveAttachmentMime,
  ATTACHMENT_LIMITS,
} from "@/lib/attachments";
import { THREAD_AGENT_OPTIONS } from "@/lib/agent-options";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import {
  filterVisibleRepositories,
  filterVisibleRepositorySessions,
} from "@/lib/repository-visibility";
import { getSessionRouteRepositoryId } from "@/lib/repoless-session-route";
import {
  consumeDashboardDraftAttachments,
  movePendingAttachmentsToDashboardDraft,
  setPendingAttachments,
} from "@/stores/pending-attachments";
import {
  consumeDashboardComposerFocusRequest,
  DASHBOARD_FOCUS_COMPOSER_EVENT as FOCUS_COMPOSER_EVENT,
} from "@/lib/dashboard-focus-signal";
import { warmNewSessionFileTreeFromRepoSnapshot } from "@/lib/new-session-file-tree-hydration";
import {
  AttachedPullRequestReference,
} from "@/components/chat/AttachedPullRequestReference";
import {
  fetchSessionList,
  getSessionListQueryKey,
  removeSessionListCacheItem,
  upsertSessionListCacheItem,
} from "@/lib/session-list-query";
import {
  PromptEditor,
  type PromptEditorHandle,
  type MentionablesResponse,
  type PrPickerItem,
  type SessionPickerItem,
  type SlashCommandItem,
} from "@/components/chat/PromptEditor";
import { SessionContextTray } from "@/components/chat/SessionContextTray";
import { ToolReferencesTray } from "@/components/chat/ToolReferencesTray";
import { ImagePreviewProvider } from "@/components/chat/ImagePreviewProvider";
import { AttachmentTray } from "@/components/chat/AttachmentTray";
import { ThreadStatusBadge } from "@/components/ui/ThreadStatusBadge";
import type { ImageAttachment } from "@/hooks/use-image-attachments";
import {
  dedupePreferredMcpServerRefs,
  dedupeRequestedSkillRefs,
} from "@/lib/message-link-references";
import { dedupeSessionContextRefs } from "@/lib/session-context-refs";
import {
  buildCreateSessionBootstrapMessage,
  clearInitialBootstrapServerEnqueueState,
  initialBootstrapDraftStorageKey,
  normalizePendingBootstrapDraft,
  writeInitialBootstrapServerEnqueueState,
  type CreateSessionBootstrapMessage,
  type PendingBootstrapDraft,
  type PendingBootstrapPrAdoption,
} from "./$repoId/-session-page-helpers";
import { RepoIcon } from "@/components/layout/RepoIcon";
import { useThreadRuntimeSessionRowMirror } from "@/hooks/use-thread-runtime-session-row-mirror";
import { useThreadRuntime } from "@/stores/thread-runtime";
import { deriveSidebarThreadStatusState } from "@/lib/sidebar-thread-status-state-machine";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  fetchRepoBranches,
  fetchRepoPulls,
  getRepoBranchesQueryKey,
  getRepoOpenPullsQueryKey,
  getRepoSessionPickerQueryKey,
  REPO_BRANCHES_STALE_TIME_MS,
  REPO_PULLS_STALE_TIME_MS,
} from "@/lib/repo-data-fetches";
import {
  CreateFromPicker,
  type CreateFromSelection,
} from "@/components/create-from-picker/CreateFromPicker";
import { PostRepoDecisionCard } from "@/components/onboarding/PostRepoDecisionCard";
import { ImportPublicRepoCard, type ImportedRepositoryDto } from "@/components/repositories/ImportPublicRepoCard";
import { NewProjectCard, type CreatedProjectDto } from "@/components/repositories/NewProjectCard";
import { DemoProjectsSection } from "@/components/demos/DemoProjectsSection";
import { EmptyStateWelcome } from "@/components/onboarding/EmptyStateWelcome";
import {
  buildCreateFromSessionDraft,
  buildCreateFromPrDraft,
} from "@/components/create-from-picker/create-from-execution";
import { useScratchPadDocument } from "@/hooks/use-scratch-pad"
import type { NotesMentionItem } from "@/components/chat/NotesMentionPopover"

const DASHBOARD_INPUT_DRAFT_STORAGE_KEY = "cord:dashboard-input-draft";
const DASHBOARD_EDITOR_SESSION_ID = "dashboard";
const ACTIVE_GITHUB_APP_INSTALLATION_STATUS = "active";

interface DashboardComposerDraftSnapshot {
  repositoryId: string | null;
  draft: PendingBootstrapDraft;
}

function buildEmptyDashboardComposerDraftSnapshot(): DashboardComposerDraftSnapshot {
  return {
    repositoryId: null,
    draft: normalizePendingBootstrapDraft({ text: "" }),
  };
}

// Re-exported for any consumer that needs the event name constant.
// The canonical definition lives in `lib/dashboard-focus-signal`.
export { DASHBOARD_FOCUS_COMPOSER_EVENT } from "@/lib/dashboard-focus-signal";

function normalizeOptionalRepositoryId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function loadDashboardComposerDraft(): DashboardComposerDraftSnapshot {
  try {
    const raw = localStorage.getItem(DASHBOARD_INPUT_DRAFT_STORAGE_KEY);
    if (!raw) {
      return buildEmptyDashboardComposerDraftSnapshot();
    }
    const parsed = JSON.parse(raw) as unknown;
    const row = parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {};
    return {
      repositoryId: normalizeOptionalRepositoryId(row.repositoryId),
      draft: normalizePendingBootstrapDraft(parsed),
    };
  } catch {
    try {
      return {
        repositoryId: null,
        draft: normalizePendingBootstrapDraft({
          text: localStorage.getItem(DASHBOARD_INPUT_DRAFT_STORAGE_KEY) ?? "",
        }),
      };
    } catch {
      return buildEmptyDashboardComposerDraftSnapshot();
    }
  }
}

function persistDashboardComposerDraft(args: DashboardComposerDraftSnapshot): void {
  const { draft } = args;
  try {
    const hasContent = Boolean(
      draft.text !== null && draft.text.length > 0
      || draft.contextRefs.length > 0
      || draft.requestedSkillRefs.length > 0
      || draft.preferredMcpServerRefs.length > 0
      || draft.attachedPullRequest
      || draft.prToAdopt,
    );
    if (!hasContent) {
      localStorage.removeItem(DASHBOARD_INPUT_DRAFT_STORAGE_KEY);
      return;
    }
    localStorage.setItem(DASHBOARD_INPUT_DRAFT_STORAGE_KEY, JSON.stringify({
      repositoryId: args.repositoryId,
      ...draft,
    }));
  } catch {
    // Ignore storage failures (quota/private mode).
  }
}

/**
 * Dashboard drafts should survive refreshes, but persisting on every keystroke
 * makes `localStorage` writes part of the hot input path. A short debounce
 * keeps the draft durable without adding noticeable loss risk during typing.
 */
const DASHBOARD_DRAFT_PERSIST_DEBOUNCE_MS = 300;

function clearDashboardComposerDraft(): void {
  try {
    localStorage.removeItem(DASHBOARD_INPUT_DRAFT_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export const Route = createFileRoute("/_authed/")({
  component: DashboardPage,
});

interface Repo {
  id: string;
  name: string;
  provider?: "github" | "gitea" | string;
  githubFullName: string;
  displayName?: string | null;
  repositoryFullName?: string | null;
  language: string | null;
  defaultBranch: string;
  iconUpdatedAt?: string | null;
  lifecycleStatus?: string | null;
}

interface AgentSession {
  id: string;
  repositoryId: string | null;
  title: string | null;
  status: string;
  agentType?: AgentType;
  lifecycleStatus: string;
  createdAt: string;
  updatedAt?: string;
  prState?: string | null;
  hasHistory?: boolean;
}

interface ImagePreview {
  id: string;
  file: File;
  previewUrl: string;
  isImage: boolean;
}

function formatRecordingDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobileLayout = useMobileLayout();
  const toggleLeftSidebar = useSessionUI((s) => s.toggleLeftSidebar);
  // The `_authed` layout blocks children from rendering until the auth
  // session is ready, so `userId` is effectively always populated here.
  // We still default to an empty string so the onboarding helpers can safely
  // no-op during any transient state transitions.
  const { data: authSessionForOnboarding } = authClient.useSession();
  const userId = authSessionForOnboarding?.user?.id ?? "";
  const activeOrganizationId = authSessionForOnboarding?.session?.activeOrganizationId ?? null;
  const initialDashboardDraftSnapshot = useMemo(() => loadDashboardComposerDraft(), []);
  const initialDashboardDraft = initialDashboardDraftSnapshot.draft;
  const [draftText, setDraftText] = useState(initialDashboardDraft.text ?? "");
  const [repolessSelectionPinned, setRepolessSelectionPinned] = useState(false);
  const [dashboardSessionContextRefs, setDashboardSessionContextRefs] = useState<AgentRuntimeSessionContextRef[]>(
    initialDashboardDraft.contextRefs,
  );
  const [dashboardRequestedSkillRefs, setDashboardRequestedSkillRefs] = useState<AgentRuntimeRequestedSkillRef[]>(
    initialDashboardDraft.requestedSkillRefs,
  );
  const [dashboardPreferredMcpServerRefs, setDashboardPreferredMcpServerRefs] = useState<AgentRuntimePreferredMcpServerRef[]>(
    initialDashboardDraft.preferredMcpServerRefs,
  );
  const [dashboardScratchPadRefs, setDashboardScratchPadRefs] = useState<AgentRuntimeScratchPadRef[]>(
    initialDashboardDraft.scratchPadRefs ?? [],
  );
  const [dashboardAttachedPullRequest, setDashboardAttachedPullRequest] = useState<AgentRuntimeAttachedPullRequest | null>(
    initialDashboardDraft.attachedPullRequest,
  );
  const [dashboardPendingPrAdoption, setDashboardPendingPrAdoption] = useState<PendingBootstrapPrAdoption | null>(
    initialDashboardDraft.prToAdopt,
  );
  const [editorHasContent, setEditorHasContent] = useState(() => {
    return Boolean(
      (initialDashboardDraft.text ?? "").trim().length > 0
      || initialDashboardDraft.contextRefs.length > 0
      || initialDashboardDraft.requestedSkillRefs.length > 0
      || initialDashboardDraft.preferredMcpServerRefs.length > 0
      || initialDashboardDraft.attachedPullRequest,
    );
  });
  const [selectedRepoId, setSelectedRepoId] = useState<string | null>(initialDashboardDraftSnapshot.repositoryId);
  const [importedRepoDecision, setImportedRepoDecision] = useState<ImportedRepositoryDto | null>(null);
  const { data: orgPolicy } = useQuery<{
    allowExternalRepos: boolean;
    adminOnlyOrgRepoConnect: boolean;
    canManage: boolean;
  }>({
    queryKey: ["org-policy"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${baseUrl}/api/settings/org-policy`, { credentials: "include" });
      if (!res.ok) {
        return {
          allowExternalRepos: true,
          adminOnlyOrgRepoConnect: false,
          canManage: false,
        };
      }
      return res.json();
    },
  });
  const allowPublicImportExternalScope = orgPolicy?.allowExternalRepos ?? true;
  const allowPublicImportOrgScope = !(orgPolicy?.adminOnlyOrgRepoConnect) || Boolean(orgPolicy?.canManage);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType>(getInitialAgentType);
  const [createFromPickerOpen, setCreateFromPickerOpen] = useState(false);
  const stableSetAgent = useCallback((v: AgentType) => setSelectedAgentType(v), []);
  useDefaultAgentType(stableSetAgent, selectedRepoId);

  const [repoDropdownOpen, setRepoDropdownOpen] = useState(false);
  const [repoSearch, setRepoSearch] = useState("");
  const [highlightedIdx, setHighlightedIdx] = useState(0);

  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const [branchSearch, setBranchSearch] = useState("");
  const [branchHighlightedIdx, setBranchHighlightedIdx] = useState(0);

  // Scratch pad — only general notes available on the homepage (no active session)
  const SCRATCH_PAD_GENERAL_SESSION_ID = "__general__";
  const generalScratchPadQuery = useScratchPadDocument({ sessionId: null });
  const homepageScratchPadNoteItems = useMemo((): NotesMentionItem[] => {
    const generalContent = generalScratchPadQuery.data?.content ?? "";
    return [{
      id: "notes-general",
      sessionId: SCRATCH_PAD_GENERAL_SESSION_ID,
      label: "General Notes",
      preview: generalContent,
      isEmpty: generalContent.trim().length === 0,
    }];
  }, [generalScratchPadQuery.data?.content]);

  const [imageAttachments, setImageAttachments] = useState<ImagePreview[]>([]);

  // --- Model & thinking variant state for the dashboard composer ---
  // We use null sessionId + sandboxRunning=false because the dashboard has no
  // active sandbox — this fetches the API-level provider list only.
  const { data: dashboardProvidersData } = useProviders(null, false, selectedAgentType);
  const dashboardProviders = dashboardProvidersData?.providers ?? [];
  const dashboardLoadingModels = dashboardProvidersData?.loadingModels ?? false;

  type DashboardModelSelection = { providerID: string; modelID: string } | null;
  const [dashboardModel, setDashboardModel] = useState<DashboardModelSelection>(null);
  const [dashboardVariant, setDashboardVariant] = useState<string | null>(null);
  const [dashboardModelSelectorOpen, setDashboardModelSelectorOpen] = useState(false);
  const [dashboardVariantOpen, setDashboardVariantOpen] = useState(false);
  const dashboardVariantRef = useRef<HTMLDivElement>(null);
  const setVariantPref = useModelPrefs((s) => s.setVariant);
  const getVariantPref = useModelPrefs((s) => s.getVariant);

  // Resolve model display name
  const dashboardModelDisplay = useMemo(() => {
    if (!dashboardModel) {
      if (dashboardLoadingModels && dashboardProviders.length > 0) return "Loading models...";
      return dashboardProviders.length > 0 ? "Select model" : "default";
    }
    const provider = dashboardProviders.find((p) => p.id === dashboardModel.providerID);
    const m = provider?.models.find((candidate) => candidate.id === dashboardModel.modelID);
    return m?.name ?? dashboardModel.modelID;
  }, [dashboardModel, dashboardLoadingModels, dashboardProviders]);

  // Thinking variant options
  const dashboardCurrentModelDetail = useMemo((): ProviderModel | null => {
    if (!dashboardModel) return null;
    for (const p of dashboardProviders) {
      if (p.id === dashboardModel.providerID) {
        return p.models.find((m) => m.id === dashboardModel.modelID && m.active && p.active) ?? null;
      }
    }
    return null;
  }, [dashboardModel, dashboardProviders]);

  const dashboardModelVariants = useMemo(
    () =>
      dashboardModel
        ? getThinkingVariants(
            dashboardModel.providerID,
            dashboardModel.modelID,
            dashboardCurrentModelDetail?.variants,
          )
        : [],
    [dashboardModel, dashboardCurrentModelDetail?.variants],
  );

  const dashboardVariantOptions = useMemo(
    () => dashboardModelVariants.map((value) => ({
      value,
      label: formatThinkingVariantLabel(value),
    })),
    [dashboardModelVariants],
  );

  const dashboardNormalizedVariant = normalizeThinkingVariant(dashboardVariant);
  const dashboardEffectiveThinkingVariant = dashboardModel
    ? resolveEffectiveThinkingVariant({
        providerID: dashboardModel.providerID,
        modelID: dashboardModel.modelID,
        requestedVariant: dashboardNormalizedVariant,
        runtimeVariants: dashboardCurrentModelDetail?.variants,
      })
    : null;

  const dashboardThinkingLabel =
    dashboardEffectiveThinkingVariant === THINKING_OFF_VARIANT
      ? "Off"
      : dashboardEffectiveThinkingVariant
        ? formatThinkingVariantLabel(dashboardEffectiveThinkingVariant)
        : null;

  const showDashboardModelSelector = selectedAgentType !== "cursor";
  const showDashboardVariantSelector =
    showDashboardModelSelector && dashboardModelVariants.length > 0 && Boolean(dashboardThinkingLabel);

  // Close variant dropdown on outside click
  useEffect(() => {
    if (!dashboardVariantOpen) return;
    function handleClick(e: MouseEvent) {
      if (dashboardVariantRef.current && !dashboardVariantRef.current.contains(e.target as Node)) {
        setDashboardVariantOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dashboardVariantOpen]);

  const handleDashboardVariantChange = useCallback((next: string | null) => {
    const normalizedNext = normalizeThinkingVariant(next);
    setDashboardVariant(normalizedNext);
    if (dashboardModel) {
      setVariantPref(dashboardModel, normalizedNext ?? undefined);
    }
  }, [dashboardModel, setVariantPref]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const branchDropdownRef = useRef<HTMLDivElement>(null);
  const createFromPickerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<PromptEditorHandle>(null);
  const dashboardHistoryRef = useRef<{
    text: string;
    sessionContextRefs: AgentRuntimeSessionContextRef[];
    requestedSkillRefs: AgentRuntimeRequestedSkillRef[];
    preferredMcpServerRefs: AgentRuntimePreferredMcpServerRef[];
    scratchPadRefs: AgentRuntimeScratchPadRef[];
  }[]>([]);
  const dashboardHistoryIndexRef = useRef(-1);
  const dashboardSavedInputRef = useRef("");
  const dashboardEditorHydratedRef = useRef(false);
  const previousSelectedRepoIdRef = useRef<string | null | undefined>(undefined);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const branchSearchInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imageAttachmentsRef = useRef<ImagePreview[]>([]);
  const dashboardDraftSnapshotRef = useRef<DashboardComposerDraftSnapshot>({
    repositoryId: initialDashboardDraftSnapshot.repositoryId,
    draft: {
      text: initialDashboardDraft.text ?? "",
      clientMessageId: initialDashboardDraft.clientMessageId,
      contextRefs: initialDashboardDraft.contextRefs,
      requestedSkillRefs: initialDashboardDraft.requestedSkillRefs,
      preferredMcpServerRefs: initialDashboardDraft.preferredMcpServerRefs,
      scratchPadRefs: initialDashboardDraft.scratchPadRefs ?? [],
      attachedPullRequest: initialDashboardDraft.attachedPullRequest,
      prToAdopt: initialDashboardDraft.prToAdopt,
    },
  });

  // Voice transcription
  const handleVoiceTranscript = useCallback((transcript: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    const currentText = editor.getText();
    const needsSeparator = currentText.trim().length > 0 && !currentText.endsWith("\n");
    editor.appendText(`${needsSeparator ? "\n" : ""}${transcript}`);
    editor.focusAtEnd();
  }, []);
  const {
    isSupported: voiceSupported,
    isRecording,
    isTranscribing,
    elapsedMs,
    error: voiceError,
    mediaStream,
    startRecording,
    stopRecording,
    cancelVoiceInput,
    retryTranscription,
    clearError: clearVoiceError,
    dismissError: dismissVoiceError,
  } = useVoiceTranscription({
    sessionId: "dashboard",
    onTranscript: handleVoiceTranscript,
  });
  const [voiceDetailExpanded, setVoiceDetailExpanded] = useState(false);
  const isVoiceInputBlocked = isRecording || isTranscribing;

  const handleVoiceToggle = () => {
    clearVoiceError();
    setVoiceDetailExpanded(false);
    if (isRecording) {
      stopRecording();
      return;
    }
    void startRecording();
  };

  const handleVoiceCancel = () => {
    setVoiceDetailExpanded(false);
    cancelVoiceInput();
  };

  const { data: hasProviderData } = useHasProvider();
  const { data: onboardingProgress, isLoading: onboardingProgressLoading } = useOnboardingProgress({
    userId,
    organizationId: activeOrganizationId,
  });
  const hasProvider = hasProviderData?.hasProvider !== false;
  const { preferences } = useUserPreferences();

  const { data: repos, isLoading: reposLoading } = useQuery({
    queryKey: ["repos"],
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<Repo>({ value: await res.json() });
    },
  });

  // GitHub App installations — used to decide onboarding CTA
  const apiBase = import.meta.env.VITE_API_URL || "";
  const { data: githubInstallations, isLoading: githubInstallationsLoading } = useQuery<
    { id: string; displayName?: string; ownerUserId?: string | null; status: string }[]
  >({
    queryKey: ["github-app-installations"],
    queryFn: async () => {
      const url = apiBase
        ? `${apiBase}/api/github/app/installations`
        : "/api/github/app/installations";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });
  const hasGitHubInstallations = (githubInstallations ?? []).some(
    (installation) => installation.status === ACTIVE_GITHUB_APP_INSTALLATION_STATUS,
  );

  // ---- Onboarding redirect ----
  // Redirect first-time users to /getting-started if they haven't completed
  // the critical onboarding steps. Server progress is the cross-device source
  // of truth, with browser storage kept as an immediate/fallback signal:
  // - Step 1 (Models): must be acknowledged
  // - Step 2 (GitHub): must be installed or intentionally skipped
  // - Step 3 (Repo): can be skipped, then shows a finish-setup banner instead
  const onboardingProgressReady = Boolean(activeOrganizationId) && !onboardingProgressLoading;
  const onboardingDataReady = !reposLoading && !githubInstallationsLoading && onboardingProgressReady && hasProviderData !== undefined;
  const onboardingHasProvider = hasProviderData?.hasProvider === true;
  const onboardingModelsStepSeen = onboardingProgress?.modelsStepSeen === true || hasSeenModelsStep(userId);
  const onboardingComplete = onboardingProgress?.completed === true || (onboardingModelsStepSeen && onboardingHasProvider && (repos ?? []).length > 0);
  const onboardingGithubStepSkipped = onboardingProgress?.githubStepSkipped === true || hasSkippedGithubStep(userId);
  const onboardingRepoStepSkipped = onboardingProgress?.repoStepSkipped === true || hasSkippedRepoStep(userId);

  useEffect(() => {
    if (!onboardingDataReady) return;
    if (onboardingComplete) return;
    // If they intentionally skipped GitHub, let them explore the
    // empty dashboard instead of bouncing straight back to onboarding.
    if (onboardingModelsStepSeen && !hasGitHubInstallations && onboardingGithubStepSkipped) return;
    // If they've seen step 1 and have GitHub, repo selection is the only
    // remaining step. Let them stay on the dashboard only when they
    // intentionally skipped the repo step; otherwise redirect back to Step 3.
    if (onboardingModelsStepSeen && hasGitHubInstallations && onboardingRepoStepSkipped) return;
    navigate({ to: "/getting-started" });
  }, [onboardingDataReady, onboardingComplete, hasGitHubInstallations, navigate, onboardingModelsStepSeen, onboardingGithubStepSkipped, onboardingRepoStepSkipped]);

  const [githubConnecting, setGithubConnecting] = useState(false);
  const deferredDashboardDataEnabled = useDeferredBackgroundActivityEnabled(Boolean(selectedRepoId));
  // During onboarding we always use "org" scope so the installation is
  // shared with the whole team.  Users who need a private/personal install
  // can add one later from Settings.
  const ONBOARDING_SCOPE = "org" as const;
  const connectGitHubApp = async () => {
    setGithubConnecting(true);
    try {
      const url = apiBase
        ? `${apiBase}/api/github/app/install-url?scope=${ONBOARDING_SCOPE}&origin=onboarding`
        : `/api/github/app/install-url?scope=${ONBOARDING_SCOPE}&origin=onboarding`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        setGithubConnecting(false);
        return;
      }
      const data = await res.json() as { installUrl: string };
      window.location.href = data.installUrl;
    } catch {
      setGithubConnecting(false);
    }
  };

  const { data: allSessions } = useQuery({
    queryKey: getSessionListQueryKey(),
    queryFn: () => fetchSessionList<AgentSession>(),
  });

  const visibleRepos = useMemo(
    () => filterVisibleRepositories({
      preferences,
      repositories: repos ?? [],
    }),
    [preferences, repos],
  );
  const visibleSessions = useMemo(
    () => filterVisibleRepositorySessions({
      preferences,
      sessions: allSessions ?? [],
    }),
    [allSessions, preferences],
  );
  const repoMap = useMemo(() => {
    const map = new Map<string, Repo>();
    visibleRepos.forEach((repo) => map.set(repo.id, repo));
    return map;
  }, [visibleRepos]);
  const recentSessions = useMemo(() => {
    return [...visibleSessions]
      .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
      .slice(0, 6);
  }, [visibleSessions]);

  // Fetch branches for the selected repo
  const branchQuery = useQuery({
    queryKey: getRepoBranchesQueryKey(selectedRepoId),
    queryFn: async () => {
      if (!selectedRepoId) return null;
      return fetchRepoBranches(selectedRepoId);
    },
    enabled: !!selectedRepoId,
    staleTime: REPO_BRANCHES_STALE_TIME_MS,
  });

  const branchesData = branchQuery.data;

  const selectedRepo = visibleRepos.find((r) => r.id === selectedRepoId) ?? null;
  const defaultBranchForRepo = branchesData?.defaultBranch ?? selectedRepo?.defaultBranch ?? "main";
  const effectiveBranch = selectedBranch ?? defaultBranchForRepo;
  const isDefaultBranch = effectiveBranch === defaultBranchForRepo;

  const repoMentionablesQuery = useQuery({
    queryKey: ["dashboard-repo-mentionables", selectedRepoId, effectiveBranch, selectedAgentType],
    queryFn: async (): Promise<MentionablesResponse> => {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const url = new URL(
        `${baseUrl}/api/repos/${encodeURIComponent(selectedRepoId ?? "")}/mentionables`,
        window.location.origin,
      );
      url.searchParams.set("branch", effectiveBranch);
      url.searchParams.set("agentType", selectedAgentType);

      const response = await fetch(url.toString(), {
        credentials: "include",
      });
      if (!response.ok) {
        const details = await response.text().catch(() => "");
        throw new Error(details || "Failed to load dashboard mentionables");
      }
      return response.json() as Promise<MentionablesResponse>;
    },
    enabled: !!selectedRepoId && deferredDashboardDataEnabled,
    staleTime: 30_000,
  });

  const repoPullsQuery = useQuery({
    queryKey: getRepoOpenPullsQueryKey(selectedRepoId),
    queryFn: async (): Promise<PrPickerItem[]> => {
      if (!selectedRepoId) return [];
      return fetchRepoPulls(selectedRepoId);
    },
    enabled: !!selectedRepoId && deferredDashboardDataEnabled,
    staleTime: REPO_PULLS_STALE_TIME_MS,
  });

  const dashboardMentionableSkills = repoMentionablesQuery.data?.skills ?? [];
  const dashboardMentionableMcpServers = repoMentionablesQuery.data?.mcpServers ?? [];

  const clearDashboardRepoScopedState = useCallback(() => {
    setDashboardSessionContextRefs([]);
    setDashboardRequestedSkillRefs([]);
    setDashboardPreferredMcpServerRefs([]);
    setDashboardScratchPadRefs([]);
    setDashboardAttachedPullRequest(null);
    setDashboardPendingPrAdoption(null);
  }, []);

  // Auto-select repo based on most recent session
  useEffect(() => {
    if (visibleRepos.length === 0) {
      if (selectedRepoId !== null) {
        setSelectedRepoId(null);
      }
      return;
    }

    const selectedRepoStillExists =
      selectedRepoId !== null &&
      visibleRepos.some((repo) => repo.id === selectedRepoId);

    if (selectedRepoStillExists) return;
    if (selectedRepoId === null && repolessSelectionPinned) return;

    const repoIds = new Set(visibleRepos.map((repo) => repo.id));
    let latestSessionRepoId: string | null = null;
    let latestSessionCreatedAt = 0;

    for (const session of visibleSessions) {
      if (!session.repositoryId) continue;
      if (!repoIds.has(session.repositoryId)) continue;
      const createdAtMs = new Date(session.createdAt).getTime();
      if (createdAtMs > latestSessionCreatedAt) {
        latestSessionCreatedAt = createdAtMs;
        latestSessionRepoId = session.repositoryId;
      }
    }

    setSelectedRepoId(
      initialDashboardDraftSnapshot.repositoryId
      && visibleRepos.some((repo) => repo.id === initialDashboardDraftSnapshot.repositoryId)
        ? initialDashboardDraftSnapshot.repositoryId
        : latestSessionRepoId ?? visibleRepos[0].id,
    );
  }, [initialDashboardDraftSnapshot.repositoryId, repolessSelectionPinned, selectedRepoId, visibleRepos, visibleSessions]);

  // Reset branch when repo changes
  useEffect(() => {
    setSelectedBranch(null);
    setCreateFromPickerOpen(false);
  }, [selectedRepoId]);

  useEffect(() => {
    if (selectedRepoId === null) {
      return;
    }

    const previousSelectedRepoId = previousSelectedRepoIdRef.current;
    if (previousSelectedRepoId === undefined) {
      previousSelectedRepoIdRef.current = selectedRepoId;
      if (
        (initialDashboardDraftSnapshot.repositoryId !== null
          && initialDashboardDraftSnapshot.repositoryId !== selectedRepoId)
        || (
          initialDashboardDraftSnapshot.repositoryId === null
          && (
            dashboardSessionContextRefs.length > 0
            || dashboardRequestedSkillRefs.length > 0
            || dashboardPreferredMcpServerRefs.length > 0
            || dashboardAttachedPullRequest !== null
            || dashboardPendingPrAdoption !== null
          )
        )
      ) {
        clearDashboardRepoScopedState();
      }
      return;
    }

    if (previousSelectedRepoId !== selectedRepoId) {
      clearDashboardRepoScopedState();
      previousSelectedRepoIdRef.current = selectedRepoId;
    }
  }, [
    clearDashboardRepoScopedState,
    dashboardAttachedPullRequest,
    dashboardPendingPrAdoption,
    dashboardPreferredMcpServerRefs.length,
    dashboardRequestedSkillRefs.length,
    dashboardSessionContextRefs.length,
    initialDashboardDraftSnapshot.repositoryId,
    selectedRepoId,
  ]);

  useEffect(() => {
    const recoveredFiles = consumeDashboardDraftAttachments();
    if (recoveredFiles.length === 0) return;
    setImageAttachments((prev) => {
      if (prev.length > 0) return prev;
      const recoveredItems: ImagePreview[] = recoveredFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        previewUrl: isAllowedImageMime(resolveAttachmentMime(file)) ? URL.createObjectURL(file) : "",
        isImage: isAllowedImageMime(resolveAttachmentMime(file)),
      }));
      return recoveredItems;
    });
  }, []);

  // Close repo dropdown on outside click
  useEffect(() => {
    if (!repoDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setRepoDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [repoDropdownOpen]);

  // Close branch dropdown on outside click
  useEffect(() => {
    if (!branchDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        branchDropdownRef.current &&
        !branchDropdownRef.current.contains(e.target as Node)
      ) {
        setBranchDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [branchDropdownOpen]);

  useEffect(() => {
    if (!createFromPickerOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        createFromPickerRef.current &&
        !createFromPickerRef.current.contains(e.target as Node)
      ) {
        setCreateFromPickerOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setCreateFromPickerOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [createFromPickerOpen]);

  useEffect(() => {
    if (repoDropdownOpen) {
      setRepoSearch("");
      setHighlightedIdx(0);
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [repoDropdownOpen]);

  useEffect(() => {
    if (branchDropdownOpen) {
      setBranchSearch("");
      setBranchHighlightedIdx(0);
      setTimeout(() => branchSearchInputRef.current?.focus(), 0);
    }
  }, [branchDropdownOpen]);

  const createSessionPending = useRef(false);
  const dashboardMountedRef = useRef(true);
  const currentDashboardDraftSnapshot = useMemo<DashboardComposerDraftSnapshot>(() => ({
    repositoryId: selectedRepoId,
    draft: {
      text: draftText,
      clientMessageId: null,
      contextRefs: dashboardSessionContextRefs,
      requestedSkillRefs: dashboardRequestedSkillRefs,
      preferredMcpServerRefs: dashboardPreferredMcpServerRefs,
      scratchPadRefs: dashboardScratchPadRefs,
      attachedPullRequest: dashboardAttachedPullRequest,
      prToAdopt: dashboardPendingPrAdoption,
    },
  }), [
    dashboardAttachedPullRequest,
    dashboardPendingPrAdoption,
    dashboardPreferredMcpServerRefs,
    dashboardRequestedSkillRefs,
    dashboardScratchPadRefs,
    dashboardSessionContextRefs,
    draftText,
    selectedRepoId,
  ]);
  dashboardDraftSnapshotRef.current = currentDashboardDraftSnapshot;
  const flushDashboardComposerDraftPersistence = useCallback(() => {
    persistDashboardComposerDraft(dashboardDraftSnapshotRef.current);
  }, []);
  const clearDashboardComposerDraftPersistence = useCallback(() => {
    dashboardDraftSnapshotRef.current = buildEmptyDashboardComposerDraftSnapshot();
    clearDashboardComposerDraft();
  }, []);

  useEffect(() => {
    return () => {
      flushDashboardComposerDraftPersistence();
      dashboardMountedRef.current = false;
    };
  }, [flushDashboardComposerDraftPersistence]);

  useEffect(() => {
    const handlePageHide = () => {
      flushDashboardComposerDraftPersistence();
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [flushDashboardComposerDraftPersistence]);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      flushDashboardComposerDraftPersistence();
    }, DASHBOARD_DRAFT_PERSIST_DEBOUNCE_MS);

    return () => window.clearTimeout(timerId);
  }, [
    currentDashboardDraftSnapshot,
    flushDashboardComposerDraftPersistence,
  ]);

  const resetDashboardComposer = useCallback((options?: { clearAttachments?: boolean }) => {
    setDraftText("");
    setDashboardSessionContextRefs([]);
    setDashboardRequestedSkillRefs([]);
    setDashboardPreferredMcpServerRefs([]);
    setDashboardScratchPadRefs([]);
    setDashboardAttachedPullRequest(null);
    setDashboardPendingPrAdoption(null);
    setEditorHasContent(false);
    dashboardHistoryRef.current = [];
    dashboardHistoryIndexRef.current = -1;
    dashboardSavedInputRef.current = "";
    dashboardEditorHydratedRef.current = true;
    clearDashboardComposerDraftPersistence();
    editorRef.current?.clear();

    if (options?.clearAttachments) {
      setImageAttachments((previousAttachments) => {
        previousAttachments.forEach((attachment) => {
          if (attachment.previewUrl) {
            URL.revokeObjectURL(attachment.previewUrl);
          }
        });
        return [];
      });
    }
  }, [clearDashboardComposerDraftPersistence]);

  const createSession = useMutation({
    mutationFn: async ({ repoId, agentType, sessionId, title, baseBranch, inheritBranch, bootstrapMessage, sessionModelIntent }: { repoId: string | null; agentType: AgentType; sessionId: string; title: string; baseBranch?: string; inheritBranch?: string; dashboardDraftSnapshot: PendingBootstrapDraft; bootstrapMessage: CreateSessionBootstrapMessage | null; sessionModelIntent: StoredSessionIntent | null; cacheSnapshot: SessionCacheMutationSnapshot }) => {
      const res = await api.api.sessions.$post({
        json: {
          repositoryId: repoId,
          title,
          agentType,
          id: sessionId,
          baseBranch,
          inheritBranch,
          bootstrapMessage: bootstrapMessage ?? undefined,
          sessionModelIntent: sessionModelIntent ?? undefined,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error ?? "Failed to create session");
      }
      return res.json() as unknown as AgentSession & {
        bootstrapMessageQueued?: boolean;
        initReady?: boolean;
      };
    },
    onSuccess: (data, vars) => {
      if (vars.bootstrapMessage) {
        const bootstrapDraftStillPresent = sessionStorage.getItem(initialBootstrapDraftStorageKey(vars.sessionId)) !== null;
        if (bootstrapDraftStillPresent && data.bootstrapMessageQueued === true) {
          writeInitialBootstrapServerEnqueueState({
            sessionId: vars.sessionId,
            state: "queued",
          });
        } else if (bootstrapDraftStillPresent && data.initReady !== true) {
          writeInitialBootstrapServerEnqueueState({
            sessionId: vars.sessionId,
            state: "pending",
          });
        } else {
          clearInitialBootstrapServerEnqueueState(vars.sessionId);
        }
      }
      const existingSession = queryClient.getQueryData<AgentSession>(["session", data.id]);
      const mergedSession = mergeSessionRecordPreservingHasHistory({
        incoming: data as AgentSession & Record<string, unknown>,
        existing: existingSession as (AgentSession & Record<string, unknown>) | undefined,
      }) as AgentSession;
      queryClient.setQueryData<AgentSession | undefined>(["session", data.id], mergedSession);
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot: vars.cacheSnapshot,
        sessionId: data.id,
        item: mergedSession as AgentSession & Record<string, unknown>,
      });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      if (vars.repoId) {
        queryClient.invalidateQueries({ queryKey: ["sessions", vars.repoId] });
        queryClient.invalidateQueries({ queryKey: getRepoSessionPickerQueryKey(vars.repoId) });
      }
    },
    onError: (err, vars) => {
      captureAnalyticsEvent({
        event: ANALYTICS_EVENTS.threadCreateFailed,
        properties: buildThreadAnalyticsProperties({
          sessionId: vars.sessionId,
          repositoryId: vars.repoId,
          agentType: vars.agentType,
          source: "dashboard",
          properties: {
            error_message: err instanceof Error ? err.message : "Failed to create session",
          },
        }),
      });
      restoreSessionCacheMutationSnapshot({
        queryClient,
        sessionId: vars.sessionId,
        snapshot: vars.cacheSnapshot,
      });
      movePendingAttachmentsToDashboardDraft(vars.sessionId);
      persistDashboardComposerDraft({
        repositoryId: vars.repoId,
        draft: vars.dashboardDraftSnapshot,
      });
      clearInitialBootstrapServerEnqueueState(vars.sessionId);
      sessionStorage.removeItem(initialBootstrapDraftStorageKey(vars.sessionId));
      sessionStorage.removeItem(`cord:session-title:${vars.sessionId}`);
      sessionStorage.removeItem(`cord:session-agent-type:${vars.sessionId}`);
      if (!dashboardMountedRef.current) {
        return;
      }
      if (err instanceof Error && err.message === "NO_PROVIDER_CONFIGURED") {
        navigate({ to: "/settings", search: { tab: "api-keys" as any } });
      } else {
        toast.error(err instanceof Error ? err.message : "Failed to create session");
        navigate({ to: "/" });
      }
    },
    onSettled: () => {
      createSessionPending.current = false;
    },
  });

  const canSend = editorHasContent || imageAttachments.length > 0 || dashboardAttachedPullRequest !== null;

  const filteredRepos = useMemo(() => {
    if (visibleRepos.length === 0) return [];
    const q = repoSearch.trim();
    if (!q) return visibleRepos;
    return fuzzysort.go(q, visibleRepos, { key: "name", threshold: -200 }).map((r) => r.obj);
  }, [repoSearch, visibleRepos]);

  const filteredBranches = useMemo(() => {
    if (!branchesData?.branches) return [];
    const query = branchSearch.trim();
    if (!query) return branchesData.branches;
    return fuzzysort.go(query, branchesData.branches, { key: "name", threshold: -200 }).map((r) => r.obj);
  }, [branchesData, branchSearch]);

  const dashboardSlashCommands = useMemo<SlashCommandItem[]>(() => {
    return [
      {
        name: "context",
        description: "Reference another session for context only",
        source: "builtin",
        enabled: true,
      },
      {
        name: "continue",
        description: "Continue from another session in this fresh thread",
        source: "builtin",
        enabled: true,
      },
      {
        name: "pr",
        description: "Adopt a repository pull request into this fresh thread",
        source: "builtin",
        enabled: Boolean(selectedRepoId),
        disabledReason: selectedRepoId ? undefined : "Select a project first",
      },
    ];
  }, [selectedRepoId]);

  /**
   * Bridge dashboard's lightweight `ImagePreview[]` to the
   * `ImageAttachment[]` shape expected by `AttachmentTray`.  Dashboard files
   * are local (not yet uploaded to R2), so they're treated as `status: "ready"`
   * with local blob preview URLs.
   */
  const attachmentTrayItems = useMemo<ImageAttachment[]>(
    () =>
      imageAttachments.map((item) => ({
        id: item.id,
        filename: item.file.name || "attachment",
        mime: resolveAttachmentMime(item.file) || "application/octet-stream",
        sizeBytes: item.file.size,
        status: "ready" as const,
        progress: 1,
        previewUrl: item.previewUrl || "",
        uploadBlob: item.file,
      })),
    [imageAttachments],
  );

  // Attachment handling
  const addImageFiles = useCallback((files: File[]) => {
    setImageAttachments((prev) => {
      const available = Math.max(0, ATTACHMENT_LIMITS.maxImagesPerMessage - prev.length);
      if (available === 0) {
        toast.error(`You can attach up to ${ATTACHMENT_LIMITS.maxImagesPerMessage} files per message.`);
        return prev;
      }

      const classified = files
        .map((file) => {
          const mime = resolveAttachmentMime(file);
          const isImage = isAllowedImageMime(mime);
          return {
            file,
            isAllowed: isAllowedMessageAttachmentMime(mime),
            isImage,
          };
        });
      const supported = classified.filter((item) => item.isAllowed);
      const unsupportedCount = classified.length - supported.length;
      if (unsupportedCount > 0) {
        toast.error(
          unsupportedCount === 1
            ? "One selected file is not supported. Attach an image, PDF, or text file."
            : `${unsupportedCount} selected files are not supported. Attach images, PDFs, or text files.`
        );
      }

      const nextItems: ImagePreview[] = supported
        .slice(0, available)
        .map((item) => ({
          id: crypto.randomUUID(),
          file: item.file,
          previewUrl: item.isImage ? URL.createObjectURL(item.file) : "",
          isImage: item.isImage,
        }));

      const overflowCount = Math.max(0, supported.length - nextItems.length);
      if (overflowCount > 0) {
        toast.error(
          overflowCount === 1
            ? "One file was skipped because the attachment limit was reached."
            : `${overflowCount} files were skipped because the attachment limit was reached.`
        );
      }

      if (nextItems.length === 0) return prev;
      return [...prev, ...nextItems];
    });
  }, []);

  const removeImage = useCallback((id: string) => {
    setImageAttachments((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  useEffect(() => {
    imageAttachmentsRef.current = imageAttachments;
  }, [imageAttachments]);

  useEffect(() => {
    return () => {
      imageAttachmentsRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, []);

  // Two paths for the sidebar "New" button to reset and focus the composer:
  //
  //   1. Cross-route navigation (user was on a thread page):
  //      The Sidebar sets a module-level signal via `requestDashboardComposerFocus()`
  //      before calling `navigate({ to: "/" })`.  On mount, the dashboard consumes
  //      that signal here.  This avoids the timing race where a DOM event fired in
  //      rAF would precede the listener registration.
  //
  //   2. Already on dashboard (component is already mounted):
  //      The Sidebar dispatches `DASHBOARD_FOCUS_COMPOSER_EVENT` directly.  Because
  //      the listener is already live there is no race.
  useEffect(() => {
    // Path 1: consume signal set before cross-route navigation
    if (consumeDashboardComposerFocusRequest()) {
      resetDashboardComposer({ clearAttachments: true });
      requestAnimationFrame(() => { editorRef.current?.focusAtEnd(); });
    }

    // Path 2: live DOM event for same-mount focus (user already on dashboard)
    function handleFocusComposer() {
      resetDashboardComposer({ clearAttachments: true });
      requestAnimationFrame(() => { editorRef.current?.focusAtEnd(); });
    }

    window.addEventListener(FOCUS_COMPOSER_EVENT, handleFocusComposer);
    return () => {
      window.removeEventListener(FOCUS_COMPOSER_EVENT, handleFocusComposer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty: run once on mount

  const handleDashboardSessionPickerIntent = useCallback((payload: {
    intent: "context" | "continue";
    session: SessionPickerItem;
  }) => {
    if (payload.intent === "context") {
      return true;
    }

    const continueRef: AgentRuntimeSessionContextRef = {
      localId: crypto.randomUUID(),
      sessionId: payload.session.id,
      repositoryId: payload.session.repositoryId ?? selectedRepoId,
      title: payload.session.title,
      origin: "continue_new_session",
    };

    setDashboardSessionContextRefs((previousRefs) => dedupeSessionContextRefs([
      ...previousRefs.filter((ref) => ref.origin !== "pr_linked_session" && ref.origin !== "continue_new_session"),
      continueRef,
    ]));
    setDashboardAttachedPullRequest(null);
    setDashboardPendingPrAdoption(null);
    return true;
  }, [selectedRepoId]);

  const handleDashboardPrPickerIntent = useCallback((payload: {
    intent: "pr";
    pullRequest: PrPickerItem;
  }) => {
    const attachedPullRequest: AgentRuntimeAttachedPullRequest = {
      number: payload.pullRequest.number,
      title: payload.pullRequest.title,
      url: payload.pullRequest.url,
      branchName: payload.pullRequest.branchName,
    };

    setDashboardAttachedPullRequest(attachedPullRequest);
    setDashboardPendingPrAdoption({
      number: payload.pullRequest.number,
      title: payload.pullRequest.title,
      branchName: payload.pullRequest.branchName,
      url: payload.pullRequest.url,
      linkedSession: payload.pullRequest.linkedSession ?? null,
    });
    setDashboardSessionContextRefs((previousRefs) => {
      const withoutSpecialRefs = previousRefs.filter((ref) => {
        return ref.origin !== "continue_new_session" && ref.origin !== "pr_linked_session";
      });

      if (!payload.pullRequest.linkedSession?.id) {
        return withoutSpecialRefs;
      }

      return dedupeSessionContextRefs([
        ...withoutSpecialRefs,
        {
          localId: crypto.randomUUID(),
          sessionId: payload.pullRequest.linkedSession.id,
          repositoryId: selectedRepoId,
          title: payload.pullRequest.linkedSession.title,
          origin: "pr_linked_session",
        },
      ]);
    });
    return true;
  }, [selectedRepoId]);

  const handleRemoveDashboardSessionContextRef = useCallback((ref: AgentRuntimeSessionContextRef, index: number) => {
    setDashboardSessionContextRefs((previousRefs) => previousRefs.filter((_, currentIndex) => currentIndex !== index));
    if (ref.origin === "continue_new_session") {
      setDashboardAttachedPullRequest(null);
      setDashboardPendingPrAdoption(null);
      return;
    }
    if (ref.origin === "pr_linked_session") {
      setDashboardPendingPrAdoption((current) => {
        if (!current) return null;
        return {
          ...current,
          linkedSession: null,
        };
      });
    }
  }, []);

  const handleRemoveDashboardRequestedSkillRef = useCallback((_: AgentRuntimeRequestedSkillRef, index: number) => {
    setDashboardRequestedSkillRefs((previousRefs) => previousRefs.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const handleRemoveDashboardPreferredMcpServerRef = useCallback((_: AgentRuntimePreferredMcpServerRef, index: number) => {
    setDashboardPreferredMcpServerRefs((previousRefs) => previousRefs.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const handleRemoveDashboardAttachedPullRequest = useCallback(() => {
    setDashboardAttachedPullRequest(null);
    setDashboardPendingPrAdoption(null);
    setDashboardSessionContextRefs((previousRefs) => {
      return previousRefs.filter((ref) => ref.origin !== "pr_linked_session");
    });
  }, []);

  const buildCurrentDashboardDraftSnapshot = useCallback((): PendingBootstrapDraft => {
    // Resolve scratch pad content from the query cache at send time
    const resolvedScratchPadRefs: AgentRuntimeScratchPadRef[] = dashboardScratchPadRefs.map((ref) => ({
      ...ref,
      content: generalScratchPadQuery.data?.content ?? "",
    }));
    return {
      text: draftText,
      clientMessageId: null,
      contextRefs: dashboardSessionContextRefs,
      requestedSkillRefs: dashboardRequestedSkillRefs,
      preferredMcpServerRefs: dashboardPreferredMcpServerRefs,
      scratchPadRefs: resolvedScratchPadRefs,
      attachedPullRequest: dashboardAttachedPullRequest,
      prToAdopt: dashboardPendingPrAdoption,
    };
  }, [
    dashboardAttachedPullRequest,
    dashboardPendingPrAdoption,
    dashboardPreferredMcpServerRefs,
    dashboardRequestedSkillRefs,
    dashboardScratchPadRefs,
    dashboardSessionContextRefs,
    draftText,
    generalScratchPadQuery.data?.content,
  ]);

  const launchDashboardSession = useCallback((args: {
    repoId: string | null;
    agentType: AgentType;
    warmBranch?: string | null;
    baseBranch?: string;
    inheritBranch?: string;
    allowBootstrapAutoSendWithoutText?: boolean;
    dashboardDraftSnapshot: PendingBootstrapDraft;
    analyticsSource: string;
  }) => {
    if (createSessionPending.current || !hasProvider) return false;
    createSessionPending.current = true;

    const sessionId = crypto.randomUUID();
    const title = formatSessionTitle();
    const attachmentCount = imageAttachments.length;
    const trimmedText = typeof args.dashboardDraftSnapshot.text === "string"
      ? args.dashboardDraftSnapshot.text.trim()
      : "";
    const hasBootstrapText = trimmedText.length > 0;
    const hasBootstrapAttachment = attachmentCount > 0;
    const hasBootstrapRefsOrPr =
      args.dashboardDraftSnapshot.contextRefs.length > 0
      || args.dashboardDraftSnapshot.requestedSkillRefs.length > 0
      || args.dashboardDraftSnapshot.preferredMcpServerRefs.length > 0
      || args.dashboardDraftSnapshot.attachedPullRequest !== null
      || args.dashboardDraftSnapshot.prToAdopt !== null;
    const bootstrapClientMessageId =
      hasBootstrapText
      || (
        args.allowBootstrapAutoSendWithoutText === true
        && (hasBootstrapAttachment || hasBootstrapRefsOrPr)
      )
        ? crypto.randomUUID()
        : null;
    const initialBootstrapDraft: PendingBootstrapDraft = {
      text: hasBootstrapText ? trimmedText : null,
      clientMessageId: bootstrapClientMessageId,
      contextRefs: args.dashboardDraftSnapshot.contextRefs,
      requestedSkillRefs: args.dashboardDraftSnapshot.requestedSkillRefs,
      preferredMcpServerRefs: args.dashboardDraftSnapshot.preferredMcpServerRefs,
      scratchPadRefs: args.dashboardDraftSnapshot.scratchPadRefs,
      attachedPullRequest: args.dashboardDraftSnapshot.attachedPullRequest,
      prToAdopt: args.dashboardDraftSnapshot.prToAdopt,
    };
    const bootstrapMessage = bootstrapClientMessageId
      ? buildCreateSessionBootstrapMessage({
          draft: initialBootstrapDraft,
          attachmentCount,
          clientMessageId: bootstrapClientMessageId,
        })
      : null;

    clearDashboardComposerDraftPersistence();
    sessionStorage.setItem(initialBootstrapDraftStorageKey(sessionId), JSON.stringify(initialBootstrapDraft));
    if (bootstrapMessage) {
      writeInitialBootstrapServerEnqueueState({ sessionId, state: "pending" });
    } else {
      clearInitialBootstrapServerEnqueueState(sessionId);
    }

    if (imageAttachments.length > 0) {
      setPendingAttachments(sessionId, imageAttachments.map((attachment) => attachment.file));
      imageAttachments.forEach((attachment) => {
        if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
      });
      setImageAttachments([]);
    }

    const optimisticSessionModelIntent: StoredSessionIntent | null = dashboardModel
      ? {
          kind: "explicit",
          model: {
            providerID: dashboardModel.providerID,
            modelID: dashboardModel.modelID,
          },
          variant: dashboardNormalizedVariant,
        }
      : null;
    const placeholder = {
      ...buildOptimisticEmptyThreadSessionRecord({
        sessionId,
        repositoryId: args.repoId,
        title,
        agentType: args.agentType,
      }),
      ...(optimisticSessionModelIntent ? { sessionModelIntent: optimisticSessionModelIntent } : {}),
    } satisfies AgentSession & { hasHistory: false } & { sessionModelIntent?: StoredSessionIntent };
    void queryClient.cancelQueries({ queryKey: ["sessions"] });
    const cacheSnapshot = captureSessionCacheMutationSnapshot({
      queryClient,
      sessionId,
      repoId: args.repoId,
    });
    applyOptimisticSessionMembershipToSessionListCaches({
      queryClient,
      snapshot: cacheSnapshot,
      sessionId,
      item: placeholder,
    });
    queryClient.setQueryData(["session", sessionId], placeholder);

    sessionStorage.setItem(`cord:session-title:${sessionId}`, title);
    sessionStorage.setItem(`cord:session-agent-type:${sessionId}`, args.agentType);

    captureAnalyticsEvent({
      event: ANALYTICS_EVENTS.threadCreated,
      properties: buildThreadAnalyticsProperties({
        sessionId,
        repositoryId: args.repoId,
        agentType: args.agentType,
        source: args.analyticsSource,
        properties: {
          has_base_branch_override: Boolean(args.baseBranch || args.inheritBranch),
          has_initial_message: trimmedText.length > 0,
          initial_attachment_count: attachmentCount,
          initial_context_ref_count: args.dashboardDraftSnapshot.contextRefs.length,
          initial_requested_skill_count: args.dashboardDraftSnapshot.requestedSkillRefs.length,
          initial_preferred_mcp_count: args.dashboardDraftSnapshot.preferredMcpServerRefs.length,
          has_initial_attached_pr: args.dashboardDraftSnapshot.attachedPullRequest !== null,
        },
      }),
    });

    // Persist the model+variant selection from the dashboard composer so the
    // new session page picks it up and configures the model selector to match.
    if (dashboardModel) {
      setExplicitSessionIntent(sessionId, {
        providerID: dashboardModel.providerID,
        modelID: dashboardModel.modelID,
        variant: dashboardNormalizedVariant,
      });
    }

    if (args.repoId) {
      warmNewSessionFileTreeFromRepoSnapshot({
        queryClient,
        repoId: args.repoId,
        sessionId,
        branch: args.warmBranch ?? null,
      });
    }

    createSession.mutate({
      repoId: args.repoId,
      agentType: args.agentType,
      sessionId,
      title,
      baseBranch: args.baseBranch,
      inheritBranch: args.inheritBranch,
      dashboardDraftSnapshot: args.dashboardDraftSnapshot,
      bootstrapMessage,
      sessionModelIntent: optimisticSessionModelIntent,
      cacheSnapshot,
    });

    navigate({
      to: "/$repoId/$sessionId",
      params: { repoId: getSessionRouteRepositoryId({ repositoryId: args.repoId }), sessionId },
    });

    return true;
  }, [
    clearDashboardComposerDraftPersistence,
    createSession,
    dashboardModel,
    dashboardNormalizedVariant,
    hasProvider,
    imageAttachments,
    navigate,
    queryClient,
  ]);

  useEffect(() => {
    if (dashboardEditorHydratedRef.current) {
      return;
    }
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    editor.setStructuredContent({
      text: draftText,
      sessionContextRefs: dashboardSessionContextRefs,
      requestedSkillRefs: dashboardRequestedSkillRefs,
      preferredMcpServerRefs: dashboardPreferredMcpServerRefs,
    });
    dashboardEditorHydratedRef.current = true;
    setEditorHasContent(
      draftText.trim().length > 0
      || dashboardSessionContextRefs.length > 0
      || dashboardRequestedSkillRefs.length > 0
      || dashboardPreferredMcpServerRefs.length > 0
      || dashboardAttachedPullRequest !== null,
    );
  }, [
    dashboardAttachedPullRequest,
    dashboardPreferredMcpServerRefs,
    dashboardRequestedSkillRefs,
    dashboardSessionContextRefs,
    draftText,
    selectedRepoId,
  ]);

  const handleSend = () => {
    if (!canSend || createSessionPending.current || !hasProvider) return;
    void launchDashboardSession({
      repoId: selectedRepoId,
      agentType: selectedAgentType,
      warmBranch: effectiveBranch,
      baseBranch: selectedRepoId && !isDefaultBranch ? effectiveBranch : undefined,
      allowBootstrapAutoSendWithoutText: true,
      analyticsSource: "dashboard",
      dashboardDraftSnapshot: buildCurrentDashboardDraftSnapshot(),
    });
  };

  const handleCreateFromSelect = useCallback((selection: CreateFromSelection) => {
    if (!selectedRepoId || createSessionPending.current || !hasProvider) return;

    if (selection.kind === "session") {
      launchDashboardSession({
        repoId: selectedRepoId,
        agentType: selection.agentType,
        warmBranch: effectiveBranch,
        dashboardDraftSnapshot: buildCreateFromSessionDraft(selection.session, selectedRepoId),
        analyticsSource: "dashboard-create-from-session",
      });
    } else if (selection.kind === "branch") {
      launchDashboardSession({
        repoId: selectedRepoId,
        agentType: selection.agentType,
        inheritBranch: selection.branch.name,
        warmBranch: selection.branch.name,
        dashboardDraftSnapshot: buildCurrentDashboardDraftSnapshot(),
        analyticsSource: "dashboard-create-from-branch",
      });
    } else {
      launchDashboardSession({
        repoId: selectedRepoId,
        agentType: selection.agentType,
        baseBranch: selection.pullRequest.baseBranch,
        warmBranch: selection.pullRequest.branchName,
        dashboardDraftSnapshot: buildCreateFromPrDraft(selection.pullRequest, selectedRepoId),
        analyticsSource: "dashboard-create-from-pr",
      });
    }
    setCreateFromPickerOpen(false);
  }, [buildCurrentDashboardDraftSnapshot, effectiveBranch, hasProvider, launchDashboardSession, selectedRepoId]);

  if (reposLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (importedRepoDecision) {
    return (
      <div className="flex h-full items-center justify-center overflow-y-auto px-4 py-8">
        <div className="w-full max-w-lg">
          <PostRepoDecisionCard
            repoId={importedRepoDecision.id}
            repoName={importedRepoDecision.displayName ?? importedRepoDecision.repositoryFullName ?? importedRepoDecision.name}
            defaultBranch={importedRepoDecision.defaultBranch ?? null}
          />
        </div>
      </div>
    );
  }

  if (!repos || repos.length === 0) {
    if (githubInstallationsLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Loader2 className="size-4 animate-spin" />
            Loading...
          </div>
        </div>
      );
    }

    return (
      <EmptyStateWelcome
        hasGitHubInstallations={hasGitHubInstallations}
        githubConnecting={githubConnecting}
        connectGitHubApp={connectGitHubApp}
        allowExternalScope={allowPublicImportExternalScope}
        allowOrgScope={allowPublicImportOrgScope}
        onProjectCreated={setImportedRepoDecision}
      />
    );
  }

  // --- Mobile: show the Codex-style thread list as the home screen ---
  if (isMobileLayout) {
    return <MobileHome />;
  }

  return (
    <ImagePreviewProvider>
    <div className="noise-overlay relative flex h-full flex-col items-center justify-center overflow-y-auto overflow-x-hidden px-6"
      style={isMobileLayout ? { paddingTop: "var(--safe-area-top, 0px)" } : undefined}
    >
      {/* Mobile menu button */}
      {isMobileLayout && (
        <button
          type="button"
          onClick={toggleLeftSidebar}
          aria-label="Open menu"
          className="absolute left-3 top-3 z-10 flex size-9 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
          style={{ top: "calc(var(--safe-area-top, 0px) + 0.5rem)" }}
        >
          <Menu className="size-[18px]" />
        </button>
      )}
      {/* Top-right three-dot menu — currently the entry point for the
          alternative AgentView UI. Lives in the corner so it doesn't compete
          with the centered hero, and stays out of the mobile menu's lane. */}
      <div
        className="absolute right-3 top-3 z-10"
        style={isMobileLayout ? { top: "calc(var(--safe-area-top, 0px) + 0.5rem)" } : undefined}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="More actions"
              className="flex size-9 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
            >
              <MoreHorizontal className="size-[18px]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={6}
            className="w-56 border-white/[0.08] bg-zinc-950"
          >
            <DropdownMenuItem asChild>
              <Link
                to="/agent-view"
                className="flex w-full cursor-pointer items-center gap-2 text-zinc-200 focus:bg-white/[0.06] focus:text-zinc-100"
              >
                <Sparkles className="size-4 text-amber-300/80" />
                <span className="flex-1">Open Agent View</span>
                <span className="rounded bg-amber-400/10 px-1.5 py-0.5 text-xs font-medium text-amber-300/80 ring-1 ring-amber-400/20">
                  fast
                </span>
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Layered background glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute left-1/2 top-[20%] size-[600px] -translate-x-1/2 rounded-full bg-zinc-500/[0.03] blur-[150px]" />
      </div>

      <div className="relative w-full max-w-[660px]">
        {/* Hero */}
        <div
          className="mb-8 text-center"
          style={{ animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) both" }}
        >
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-white sm:text-[30px]">
            What should we build?
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
            Start a new thread — your agent runs in a sandboxed environment.
          </p>
        </div>

        {/* Onboarding incomplete banner — shown after a session-scoped repo-step skip */}
        {hasGitHubInstallations && !onboardingComplete && onboardingRepoStepSkipped && (repos ?? []).length === 0 && (
          <div
            className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-zinc-700/30 bg-zinc-800/40 px-4 py-3 backdrop-blur-sm"
            style={{ animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}
          >
            <span className="text-sm text-zinc-400">
              Finish setting up your workspace to start coding.
            </span>
            <Link
              to="/getting-started"
              className="shrink-0 rounded-md bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-900 transition-colors hover:bg-white"
            >
              Continue Setup
            </Link>
          </div>
        )}

        {/* No provider warning */}
        {!hasProvider && (
          <div
            className="mb-4 flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-200/90 backdrop-blur-sm"
            style={{ animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-amber-400">
              <path d="M8 1.5L14.5 13H1.5L8 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              <path d="M8 6.5V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              <circle cx="8" cy="11" r="0.75" fill="currentColor" />
            </svg>
            <span>
              Add an API key or connect your ChatGPT subscription in{" "}
              <Link to="/settings" search={{ tab: "api-keys" as any }} className="font-medium underline underline-offset-2 text-amber-100 hover:text-white transition-colors">
                Settings
              </Link>
              {" "}to start a thread.
            </span>
          </div>
        )}

        {/* Composer */}
        <div
          className={cn(
            "relative group rounded-2xl border shadow-2xl shadow-black/20 transition-all duration-300",
            !hasProvider
              ? "border-white/[0.04] bg-white/[0.015] opacity-60 pointer-events-none"
              : repoDropdownOpen || branchDropdownOpen
                ? "border-white/[0.1] bg-white/[0.03] ring-1 ring-white/[0.06]"
                : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.08] focus-within:border-white/[0.1] focus-within:ring-1 focus-within:ring-white/[0.06]",
          )}
          style={{ animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both" }}
        >
          <div className="px-5 pt-4 pb-2">
            <PromptEditor
              ref={editorRef}
              repositoryId={selectedRepoId}
                sessionId={DASHBOARD_EDITOR_SESSION_ID}
                baseBranch={effectiveBranch}
                onSubmit={handleSend}
                canSubmit={canSend}
                onEscape={() => editorRef.current?.blur()}
                onAbort={() => undefined}
                abortHotkeysEnabled={false}
                disabled={isVoiceInputBlocked}
                placeholder={
                  isRecording
                    ? "Recording voice..."
                    : isTranscribing
                      ? "Transcribing audio..."
                      : "Describe what you want to build..."
                }
                onContentChange={setEditorHasContent}
                onTextChange={setDraftText}
                onSessionContextRefsChange={(refs) => {
                  setDashboardSessionContextRefs(dedupeSessionContextRefs(refs));
                }}
                sessionContextRefs={dashboardSessionContextRefs}
                onRequestedSkillRefsChange={(refs) => {
                  setDashboardRequestedSkillRefs(dedupeRequestedSkillRefs(refs));
                }}
                requestedSkillRefs={dashboardRequestedSkillRefs}
                onPreferredMcpServerRefsChange={(refs) => {
                  setDashboardPreferredMcpServerRefs(dedupePreferredMcpServerRefs(refs));
                }}
                preferredMcpServerRefs={dashboardPreferredMcpServerRefs}
                scratchPadNoteItems={homepageScratchPadNoteItems}
                onScratchPadRefsChange={setDashboardScratchPadRefs}
                scratchPadRefs={dashboardScratchPadRefs}
                history={dashboardHistoryRef.current}
                historyIndex={dashboardHistoryIndexRef}
                savedInput={dashboardSavedInputRef}
                onImageFiles={addImageFiles}
                slashCommands={dashboardSlashCommands}
                customMentionables={{
                  skills: dashboardMentionableSkills,
                  mcpServers: dashboardMentionableMcpServers,
                  loading: !deferredDashboardDataEnabled || repoMentionablesQuery.isLoading,
                  refreshing: repoMentionablesQuery.isFetching,
                  error: repoMentionablesQuery.isError,
                }}
                customPrPickerData={{
                  items: repoPullsQuery.data ?? [],
                  loading: !deferredDashboardDataEnabled || repoPullsQuery.isLoading,
                  refreshing: repoPullsQuery.isFetching,
                  error: repoPullsQuery.isError,
                  resolved: deferredDashboardDataEnabled && (repoPullsQuery.data !== undefined || repoPullsQuery.error != null),
                }}
                onSessionPickerIntent={handleDashboardSessionPickerIntent}
                onPrPickerIntent={handleDashboardPrPickerIntent}
                editorClassName="w-full min-h-[72px] max-h-48 overflow-y-auto bg-transparent text-[14px] leading-relaxed text-zinc-100 outline-none [overflow-wrap:anywhere]"
                placeholderClassName="pointer-events-none absolute left-0 top-0 text-[14px] text-zinc-600"
            />
          </div>

          {/* Attachment previews — uses the shared AttachmentTray for
              consistent styling and click-to-preview via ImagePreviewProvider */}
          <AttachmentTray
            attachments={attachmentTrayItems}
            onRemove={removeImage}
            onRetry={() => {
              /* Dashboard attachments are local files, not uploaded to R2 yet.
                 Retry is a no-op here; upload happens after session creation. */
            }}
          />

          <SessionContextTray
            contextRefs={dashboardSessionContextRefs}
            onRemove={handleRemoveDashboardSessionContextRef}
          />

          <ToolReferencesTray
            requestedSkillRefs={dashboardRequestedSkillRefs}
            preferredMcpServerRefs={dashboardPreferredMcpServerRefs}
            onRemoveRequestedSkillRef={handleRemoveDashboardRequestedSkillRef}
            onRemovePreferredMcpServerRef={handleRemoveDashboardPreferredMcpServerRef}
          />

          {dashboardAttachedPullRequest && (
            <div className="px-4 pb-2">
              <div className="inline-flex max-w-full items-center gap-2 rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/10 px-2 py-1.5 text-xs text-fuchsia-100">
                <AttachedPullRequestReference pullRequest={dashboardAttachedPullRequest} />
                <button
                  type="button"
                  onClick={handleRemoveDashboardAttachedPullRequest}
                  className="flex size-5 items-center justify-center rounded-full text-fuchsia-200/70 transition-colors hover:bg-fuchsia-500/15 hover:text-fuchsia-100"
                  title="Remove attached pull request"
                >
                  <X className="size-3" />
                </button>
              </div>
            </div>
          )}

          {/* Recording state */}
          {isRecording && (
            <div className="mx-4 mb-2 flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-950/20 px-3 py-1.5 text-xs text-red-200">
              <AudioWaveform
                stream={mediaStream}
                width={64}
                height={22}
                color="rgba(252,165,165,0.8)"
              />
              <span className="font-medium">Recording</span>
              <span className="tabular-nums">{formatRecordingDuration(elapsedMs)}</span>
              <button
                type="button"
                onClick={handleVoiceCancel}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-red-300/30 bg-red-500/15 px-2 py-1 font-medium text-red-100 transition-colors hover:border-red-200/50 hover:bg-red-500/25 hover:text-white"
                aria-label="Cancel voice recording"
                title="Cancel recording"
              >
                <X className="size-3.5" weight="bold" />
                <span>Cancel</span>
              </button>
            </div>
          )}

          {/* Transcribing state */}
          {isTranscribing && !voiceError && (
            <div className="mx-4 mb-2 flex items-center gap-2 rounded-lg border border-zinc-700/60 bg-zinc-900/70 px-3 py-2 text-xs text-zinc-300">
              <SpinnerGap className="size-3.5 animate-spin" />
              <span className="min-w-0 flex-1">Transcribing audio...</span>
              <button
                type="button"
                onClick={handleVoiceCancel}
                className="inline-flex items-center gap-1 rounded-md border border-white/[0.12] bg-white/[0.06] px-2 py-1 font-medium text-zinc-200 transition-colors hover:border-white/[0.2] hover:bg-white/[0.1] hover:text-white"
                aria-label="Cancel audio transcription"
                title="Cancel transcription"
              >
                <X className="size-3.5" weight="bold" />
                <span>Cancel</span>
              </button>
            </div>
          )}

          {/* Voice error with retry + expandable detail */}
          {voiceError && !isRecording && (
            <div className="mx-4 mb-2 rounded-lg border border-red-500/40 bg-red-950/20 text-xs text-red-300">
              <div className="flex items-center gap-2 px-3 py-2">
                <span className="min-w-0 flex-1 truncate">{voiceError.message}</span>
                <div className="flex shrink-0 items-center gap-1">
                  {voiceError.retryable && (
                    <button
                      type="button"
                      disabled={isTranscribing}
                      onClick={() => {
                        setVoiceDetailExpanded(false);
                        void retryTranscription();
                      }}
                      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-red-300 transition-colors hover:bg-red-500/20 disabled:opacity-40"
                    >
                      <ArrowClockwise className="size-3" />
                      <span>Retry</span>
                    </button>
                  )}
                  {voiceError.detail && (
                    <button
                      type="button"
                      onClick={() => setVoiceDetailExpanded((prev) => !prev)}
                      className="flex items-center rounded px-1 py-0.5 text-red-400/70 transition-colors hover:bg-red-500/20 hover:text-red-300"
                      title={voiceDetailExpanded ? "Hide details" : "Show details"}
                    >
                      <CaretRight
                        className={cn(
                          "size-3 transition-transform",
                          voiceDetailExpanded && "rotate-90",
                        )}
                      />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setVoiceDetailExpanded(false);
                      dismissVoiceError();
                    }}
                    className="flex items-center rounded px-0.5 py-0.5 text-red-400/70 transition-colors hover:bg-red-500/20 hover:text-red-300"
                    title="Dismiss"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              </div>
              {voiceDetailExpanded && voiceError.detail && (
                <div className="border-t border-red-500/20 px-3 py-2 font-mono text-xs leading-relaxed text-red-400/80">
                  {voiceError.detail}
                </div>
              )}
            </div>
          )}

          {/* Bottom toolbar — inline action buttons row */}
          <div className="relative px-3 pb-3">
            {/* Row 1: plus (mention) / file / mic on left, model/thinking + send on right */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1">
                {/* Hidden file input — triggered from file button */}
                <input
                  ref={imageInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    if (files.length > 0) addImageFiles(files);
                    e.target.value = "";
                  }}
                />

                {/* Plus / mention button */}
                <button
                  type="button"
                  onClick={() => {
                    editorRef.current?.appendText("@");
                    editorRef.current?.focusAtEnd();
                  }}
                  className="flex size-8 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-300"
                  title="Mention (@)"
                >
                  <Plus className="size-4" />
                </button>

                {/* File attach button */}
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="flex size-8 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-300"
                  title="Attach files"
                >
                  <FileIcon className="size-4" />
                </button>

                {/* Voice / mic button */}
                <button
                  type="button"
                  disabled={isTranscribing || !voiceSupported}
                  onClick={handleVoiceToggle}
                  className={cn(
                    "flex size-8 items-center justify-center rounded-lg transition-colors",
                    isRecording
                      ? "text-red-400 bg-red-500/10"
                      : "text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-300",
                    (isTranscribing || !voiceSupported) && "opacity-40 cursor-not-allowed",
                  )}
                  title={isRecording ? "Stop recording" : "Record voice"}
                >
                  {isTranscribing ? (
                    <SpinnerGap className="size-4 animate-spin" />
                  ) : (
                    <Microphone
                      className="size-4"
                      weight={isRecording ? "fill" : "regular"}
                    />
                  )}
                </button>

                {/* Create from… button */}
                {selectedRepoId && (
                  <button
                    type="button"
                    onClick={() => {
                      setRepoDropdownOpen(false);
                      setBranchDropdownOpen(false);
                      setCreateFromPickerOpen((prev) => !prev);
                    }}
                    className="flex size-8 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-300"
                    title="Create from…"
                  >
                    <ArrowRight className="size-4" />
                  </button>
                )}
              </div>

              {/* Right side: model + thinking + send */}
              <div className="flex items-center gap-0.5">
                {/* Model selector */}
                {selectedRepoId && showDashboardModelSelector && (
                  <ModelSelectorPopover
                    providers={dashboardProviders}
                    effectiveValue={dashboardModel}
                    open={dashboardModelSelectorOpen}
                    onOpenChange={setDashboardModelSelectorOpen}
                    onChange={(val) => {
                      setDashboardModel(val);
                      if (val) {
                        const rememberedVariant = normalizeThinkingVariant(getVariantPref(val));
                        const carriedVariant = normalizeThinkingVariant(dashboardVariant);
                        const targetModelDetail = (() => {
                          for (const provider of dashboardProviders) {
                            if (provider.id !== val.providerID) continue;
                            return provider.models.find((model) => model.id === val.modelID && model.active && provider.active) ?? null;
                          }
                          return null;
                        })();
                        const nextVariant = resolveEffectiveThinkingVariant({
                          providerID: val.providerID,
                          modelID: val.modelID,
                          requestedVariant: rememberedVariant ?? carriedVariant,
                          runtimeVariants: targetModelDetail?.variants,
                        });
                        setDashboardVariant(nextVariant);
                        setVariantPref(val, nextVariant ?? undefined);
                      } else {
                        setDashboardVariant(null);
                      }
                      window.requestAnimationFrame(() => editorRef.current?.focusAtEnd());
                    }}
                    loading={dashboardLoadingModels}
                    renderTrigger={({ onClick }) => (
                      <Tooltip content="Choose the AI model for this thread.">
                        <button
                          type="button"
                          onClick={onClick}
                          className="flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] text-zinc-500 transition-all duration-150 hover:bg-white/[0.06] hover:text-zinc-300"
                        >
                          <span className="max-w-[140px] truncate">{dashboardModelDisplay}</span>
                          <CaretDown className="size-2.5 shrink-0 opacity-50" />
                        </button>
                      </Tooltip>
                    )}
                  />
                )}

                {/* Thinking variant selector */}
                {selectedRepoId && showDashboardVariantSelector && (
                  <div className="relative" ref={dashboardVariantRef}>
                    <Tooltip content="Thinking level controls how much reasoning the model does before responding.">
                      <button
                        type="button"
                        onClick={() => setDashboardVariantOpen(!dashboardVariantOpen)}
                        className="flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] text-zinc-500 transition-all duration-150 hover:bg-white/[0.06] hover:text-zinc-300"
                      >
                        <Brain className="size-3 shrink-0 opacity-60" />
                        <span>
                          {dashboardNormalizedVariant === THINKING_OFF_VARIANT ? "Off" : (dashboardThinkingLabel ?? "Off")}
                        </span>
                        <CaretDown className="size-2.5 shrink-0 opacity-50" />
                      </button>
                    </Tooltip>
                    {dashboardVariantOpen && (
                      <div className="absolute bottom-full right-0 z-50 mb-1.5 w-40 overflow-hidden rounded-xl border border-white/[0.08] bg-zinc-950 py-1 shadow-2xl">
                        <button
                          type="button"
                          onClick={() => { handleDashboardVariantChange(THINKING_OFF_VARIANT); setDashboardVariantOpen(false); }}
                          className={cn(
                            "flex w-full items-center px-3 py-1.5 text-left text-xs transition-colors hover:bg-white/[0.06]",
                            dashboardNormalizedVariant === THINKING_OFF_VARIANT ? "text-zinc-100 font-medium" : "text-zinc-400",
                          )}
                        >
                          Off
                        </button>
                        {dashboardVariantOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => { handleDashboardVariantChange(opt.value); setDashboardVariantOpen(false); }}
                            className={cn(
                              "flex w-full items-center px-3 py-1.5 text-left text-xs transition-colors hover:bg-white/[0.06]",
                              dashboardNormalizedVariant === opt.value ? "text-zinc-100 font-medium" : "text-zinc-400",
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Send */}
                <button
                  onClick={handleSend}
                  disabled={!canSend}
                  className={cn(
                    "flex size-8 items-center justify-center rounded-full transition-all duration-200",
                    canSend
                      ? "bg-white text-zinc-950 hover:bg-zinc-200 active:scale-95"
                      : "bg-white/[0.04] text-zinc-600 cursor-not-allowed",
                  )}
                >
                  <ArrowUp className="size-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>

            {/* CreateFromPicker overlay — anchored above toolbar */}
            {createFromPickerOpen && selectedRepoId && (
              <div
                ref={createFromPickerRef}
                className="absolute bottom-full right-3 z-50 mb-2"
              >
                <CreateFromPicker
                  repositoryId={selectedRepoId}
                  defaultAgentType={selectedAgentType}
                  onClose={() => setCreateFromPickerOpen(false)}
                  onSelect={handleCreateFromSelect}
                />
              </div>
            )}
          </div>
        </div>

        {/* Row 2: selectors below the input box */}
        <div
          className="mt-3 flex flex-wrap items-center justify-between gap-2"
          style={{ animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both" }}
        >
          <div className="flex items-center gap-1.5 min-w-0 overflow-visible">
              {/* Project selector trigger */}
              <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => {
                      setRepoDropdownOpen(!repoDropdownOpen);
                      setCreateFromPickerOpen(false);
                      setBranchDropdownOpen(false);
                    }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all duration-200",
                    repoDropdownOpen
                      ? "border-white/[0.12] bg-white/[0.06] text-zinc-200"
                      : "border-white/[0.06] bg-white/[0.03] text-zinc-400 hover:border-white/[0.1] hover:bg-white/[0.05] hover:text-zinc-300",
                  )}
                >
                  {selectedRepo && (
                    <RepoIcon
                      repositoryId={selectedRepo.id}
                      iconUpdatedAt={selectedRepo.iconUpdatedAt}
                      className="size-3.5 shrink-0"
                      fallbackClassName="text-zinc-400"
                    />
                  )}
                  <span className="max-w-[120px] truncate">
                    {selectedRepo?.name ?? "No repo attached"}
                  </span>
                  <ChevronDown
                    className={cn(
                      "size-2.5 opacity-60 transition-transform duration-200",
                      repoDropdownOpen && "rotate-180",
                    )}
                  />
                </button>

                {/* Repo Dropdown */}
                {repoDropdownOpen && (
                  <div className="absolute bottom-full left-0 z-50 mb-2 w-72 max-w-[calc(100vw-2rem)] animate-[slide-in-down_0.12s_ease-out] rounded-xl border border-white/[0.08] bg-zinc-950 shadow-2xl shadow-black/50 overflow-hidden">
                    {/* Search field */}
                    <div className="flex items-center gap-2.5 border-b border-white/[0.06] px-3.5 py-2.5">
                      <Search className="size-3.5 shrink-0 text-zinc-500" />
                      <input
                        ref={searchInputRef}
                        value={repoSearch}
                        onChange={(e) => {
                          setRepoSearch(e.target.value);
                          setHighlightedIdx(0);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlightedIdx((i) =>
                              Math.min(i + 1, filteredRepos.length - 1),
                            );
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlightedIdx((i) => Math.max(i - 1, 0));
                          } else if (e.key === "Enter") {
                            e.preventDefault();
                            const repo = filteredRepos[highlightedIdx];
                            if (repo) {
                              setSelectedRepoId(repo.id);
                              setRepolessSelectionPinned(false);
                              setRepoDropdownOpen(false);
                              editorRef.current?.focusAtEnd();
                            }
                          } else if (e.key === "Escape") {
                            setRepoDropdownOpen(false);
                            editorRef.current?.focusAtEnd();
                          }
                        }}
                        placeholder="Search projects..."
                        className="w-full bg-transparent text-sm text-zinc-200 placeholder-zinc-600 outline-none"
                      />
                    </div>

                    {/* Results */}
                    <div className="max-h-56 overflow-y-auto py-1.5">
                      {(repoSearch.trim().length === 0 || "no repo attached".includes(repoSearch.trim().toLowerCase())) && (
                        <button
                          onClick={() => {
                            setSelectedRepoId(null);
                            setRepolessSelectionPinned(true);
                            setRepoDropdownOpen(false);
                            editorRef.current?.focusAtEnd();
                          }}
                          className={cn(
                            "flex w-full items-center gap-3 px-3.5 py-2 text-left transition-colors",
                            selectedRepoId === null ? "bg-zinc-800/70 text-zinc-100" : "text-zinc-400 hover:bg-zinc-800/70 hover:text-zinc-100",
                          )}
                        >
                          <GitBranch className="size-3.5 shrink-0 text-zinc-600" />
                          <span className="min-w-0 flex-1 truncate text-sm">No repo attached</span>
                          {selectedRepoId === null && <Check className="size-3.5 shrink-0 text-zinc-400" />}
                        </button>
                      )}
                      {filteredRepos.length === 0 ? (
                        <div className="px-4 py-4 text-center text-[12px] text-zinc-600">
                          No projects match &ldquo;{repoSearch}&rdquo;
                        </div>
                      ) : (
                        filteredRepos.map((repo, idx) => {
                          const isSelected = repo.id === selectedRepoId;
                          const isHighlighted = idx === highlightedIdx;
                          return (
                            <button
                              key={repo.id}
                              onClick={() => {
                                setSelectedRepoId(repo.id);
                                setRepolessSelectionPinned(false);
                                setRepoDropdownOpen(false);
                                editorRef.current?.focusAtEnd();
                              }}
                              onMouseEnter={() => setHighlightedIdx(idx)}
                              className={cn(
                                "flex w-full items-center gap-3 px-3.5 py-2 text-left transition-colors",
                                isHighlighted && "bg-zinc-800/70",
                              )}
                            >
                              <RepoIcon
                                repositoryId={repo.id}
                                iconUpdatedAt={repo.iconUpdatedAt}
                                className="size-3.5 shrink-0"
                                fallbackClassName="text-zinc-600"
                              />
                              <span
                                className={cn(
                                  "min-w-0 flex-1 truncate text-sm",
                                  isSelected || isHighlighted
                                    ? "text-zinc-100"
                                    : "text-zinc-400",
                                )}
                              >
                                {repo.name}
                              </span>
                              {repo.language && (
                                <span className="shrink-0 text-xs text-zinc-600">
                                  {repo.language}
                                </span>
                              )}
                              {isSelected && (
                                <Check className="size-3.5 shrink-0 text-zinc-400" />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-white/[0.06] p-1.5">
                      <Link
                        to="/repos"
                        search={{ disconnectRepoId: undefined }}
                        className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[12px] text-zinc-500 transition-colors hover:bg-white/[0.04] hover:text-zinc-300"
                      >
                        <Plus className="size-3.5" />
                        Add another project
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Branch selector */}
              {selectedRepoId && (
                <div className="relative" ref={branchDropdownRef}>
                  <button
                    onClick={() => {
                      setBranchDropdownOpen(!branchDropdownOpen);
                      setCreateFromPickerOpen(false);
                      setRepoDropdownOpen(false);
                    }}
                    className={cn(
                      "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all duration-200",
                      branchDropdownOpen
                        ? "border-white/[0.12] bg-white/[0.06] text-zinc-200"
                        : isDefaultBranch
                          ? "border-white/[0.06] bg-white/[0.03] text-zinc-500 hover:border-white/[0.1] hover:text-zinc-400"
                          : "border-white/[0.1] bg-white/[0.04] text-zinc-300 hover:border-white/[0.14] hover:text-zinc-200",
                    )}
                  >
                    <GitBranch className="size-3 shrink-0" />
                    <span className="max-w-[100px] truncate">
                      {effectiveBranch}
                    </span>
                    <ChevronDown
                      className={cn(
                        "size-2.5 opacity-60 transition-transform duration-200",
                        branchDropdownOpen && "rotate-180",
                      )}
                    />
                  </button>

                  {/* Branch Dropdown */}
                  {branchDropdownOpen && (
                    <div className="absolute bottom-full left-0 z-50 mb-2 w-64 max-w-[calc(100vw-2rem)] animate-[slide-in-down_0.12s_ease-out] rounded-xl border border-white/[0.08] bg-zinc-950 shadow-2xl shadow-black/50 overflow-hidden">
                      {/* Search field */}
                      <div className="flex items-center gap-2.5 border-b border-white/[0.06] px-3.5 py-2.5">
                        <Search className="size-3.5 shrink-0 text-zinc-500" />
                        <input
                          ref={branchSearchInputRef}
                          value={branchSearch}
                          onChange={(e) => {
                            setBranchSearch(e.target.value);
                            setBranchHighlightedIdx(0);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowDown") {
                              e.preventDefault();
                              setBranchHighlightedIdx((i) =>
                                Math.min(i + 1, filteredBranches.length - 1),
                              );
                            } else if (e.key === "ArrowUp") {
                              e.preventDefault();
                              setBranchHighlightedIdx((i) => Math.max(i - 1, 0));
                            } else if (e.key === "Enter") {
                              e.preventDefault();
                              const branch = filteredBranches[branchHighlightedIdx];
                              if (branch) {
                                setSelectedBranch(branch.isDefault ? null : branch.name);
                                setBranchDropdownOpen(false);
                                editorRef.current?.focusAtEnd();
                              }
                            } else if (e.key === "Escape") {
                              setBranchDropdownOpen(false);
                              editorRef.current?.focusAtEnd();
                            }
                          }}
                          placeholder="Search branches..."
                          className="w-full bg-transparent text-sm text-zinc-200 placeholder-zinc-600 outline-none"
                        />
                      </div>

                      {/* Results */}
                      <div className="max-h-56 overflow-y-auto py-1.5">
                        {branchQuery.isError ? (
                          <div className="px-4 py-4 text-center text-[12px] text-amber-200/80">
                            <div>Couldn&apos;t load branches right now.</div>
                            <button
                              type="button"
                              onClick={() => void branchQuery.refetch()}
                              className="mt-2 inline-flex items-center rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 transition-colors hover:bg-zinc-800"
                            >
                              Retry
                            </button>
                          </div>
                        ) : branchQuery.isPending && !branchesData ? (
                          <div className="flex items-center justify-center gap-2 px-4 py-4 text-[12px] text-zinc-600">
                            <SpinnerGap className="size-3.5 animate-spin" />
                            <span>Loading branches...</span>
                          </div>
                        ) : filteredBranches.length === 0 ? (
                          <div className="px-4 py-4 text-center text-[12px] text-zinc-600">
                            No branches match &ldquo;{branchSearch}&rdquo;
                          </div>
                        ) : (
                          filteredBranches.map((branch, idx) => {
                            const isSelected = branch.name === effectiveBranch;
                            const isHighlighted = idx === branchHighlightedIdx;
                            return (
                              <button
                                key={branch.name}
                                onClick={() => {
                                  setSelectedBranch(branch.isDefault ? null : branch.name);
                                  setBranchDropdownOpen(false);
                                  editorRef.current?.focusAtEnd();
                                }}
                                onMouseEnter={() => setBranchHighlightedIdx(idx)}
                                className={cn(
                                  "flex w-full items-center gap-3 px-3.5 py-2 text-left transition-colors",
                                  isHighlighted && "bg-zinc-800/70",
                                )}
                              >
                                <GitBranch className="size-3 shrink-0 text-zinc-600" />
                                <span
                                  className={cn(
                                    "min-w-0 flex-1 truncate text-sm",
                                    isSelected || isHighlighted
                                      ? "text-zinc-100"
                                      : "text-zinc-400",
                                  )}
                                >
                                  {branch.name}
                                </span>
                                {branch.isDefault && (
                                  <span className="shrink-0 rounded-full bg-zinc-800 px-1.5 py-0.5 text-xs text-zinc-500">
                                    default
                                  </span>
                                )}
                                {isSelected && (
                                  <Check className="size-3.5 shrink-0 text-zinc-400" />
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Agent type toggle — uses the same border + py-1 height as repo/branch pills */}
              {selectedRepoId && (
                <div className="flex items-center rounded-full border border-white/[0.06] bg-white/[0.02]">
                  {THREAD_AGENT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSelectedAgentType(option.value)}
                      className={cn(
                        "rounded-full px-2.5 py-1 text-[11px] font-medium transition-all duration-200",
                        selectedAgentType === option.value
                          ? "bg-white/[0.08] text-zinc-300 shadow-sm"
                          : "text-zinc-600 hover:text-zinc-400",
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
          </div>
        </div>

        {/* Recent threads */}
        {recentSessions.length > 0 && (
          <div
            className="mt-10"
            style={{ animation: "hero-reveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both" }}
          >
            <div className="mb-3 flex items-center gap-2 px-1">
              <div className="h-px flex-1 bg-gradient-to-r from-white/[0.06] to-transparent" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-600">
                Recent threads
              </span>
              <div className="h-px flex-1 bg-gradient-to-l from-white/[0.06] to-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {recentSessions.map((s) => (
                <DashboardRecentThreadCard
                  key={s.id}
                  session={s}
                  repoName={s.repositoryId ? repoMap.get(s.repositoryId)?.name ?? null : "No repo attached"}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </ImagePreviewProvider>
  );
}

function DashboardRecentThreadCard({
  session,
  repoName,
}: {
  session: AgentSession;
  repoName: string | null;
}) {
  useThreadRuntimeSessionRowMirror(session)
  const runtimeEntry = useThreadRuntime((state) => state.entries[session.id]);
  const threadStatus = deriveSidebarThreadStatusState({
    entry: runtimeEntry,
    persistedStatus: session.status,
    lifecycleStatus: session.lifecycleStatus,
    prState: session.prState,
  });
  const waitingForReview = threadStatus.needsAttention;

  return (
    <Link
      to="/$repoId/$sessionId"
      params={{ repoId: getSessionRouteRepositoryId({ repositoryId: session.repositoryId }), sessionId: session.id }}
      className={cn(
        "group flex items-start gap-3 rounded-xl border px-3.5 py-3.5 transition-all duration-200",
        waitingForReview
          ? "border-sky-500/20 bg-sky-500/[0.04] hover:border-sky-400/30 hover:bg-sky-500/[0.08]"
          : "border-white/[0.04] bg-white/[0.015] hover:border-white/[0.08] hover:bg-white/[0.03]",
      )}
    >
      <ThreadStatusBadge status={threadStatus} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div
          title={session.title ?? "Untitled"}
          className={cn(
            "truncate text-sm font-medium transition-colors",
            waitingForReview
              ? "text-zinc-200 group-hover:text-zinc-100"
              : "text-zinc-400 group-hover:text-zinc-200",
          )}
        >
          {session.title ?? "Untitled"}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-zinc-600">
          {repoName ? <span className="truncate">{repoName}</span> : null}
          <span className="shrink-0 tabular-nums">
            {relativeTime(session.createdAt)}
          </span>
        </div>
      </div>
      <ArrowRight className="mt-1 size-3 shrink-0 text-zinc-700 opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 group-hover:text-zinc-500" />
    </Link>
  );
}
